import React, { useState, useCallback, FormEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, Loader2 } from 'lucide-react';
import { ValidationRule, FormValidator } from '../../utils/validation';
import { Button } from './';

interface ValidatedFormProps {
  children: React.ReactNode;
  validationRules: Record<string, ValidationRule>;
  onSubmit: (data: Record<string, any>) => void | Promise<void>;
  className?: string;
  submitButtonText?: string;
  submitButtonClassName?: string;
  showErrorSummary?: boolean;
  disabled?: boolean;
}

const ValidatedForm: React.FC<ValidatedFormProps> = ({
  children,
  validationRules,
  onSubmit,
  className = '',
  submitButtonText = 'Submit',
  submitButtonClassName = '',
  showErrorSummary = true,
  disabled = false,
}) => {
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>('');

  const validator = new FormValidator(validationRules);

  const updateFormData = useCallback((name: string, value: any) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const updateTouched = useCallback((name: string) => {
    setTouched(prev => ({ ...prev, [name]: true }));
  }, []);

  const validateForm = useCallback(() => {
    const result = validator.validate(formData);
    const errorMap: Record<string, string> = {};

    result.errors.forEach(error => {
      errorMap[error.field] = error.message;
    });

    setErrors(errorMap);

    // Mark all fields as touched
    const allTouched = Object.keys(validationRules).reduce(
      (acc, field) => {
        acc[field] = true;
        return acc;
      },
      {} as Record<string, boolean>
    );
    setTouched(allTouched);

    return result.isValid;
  }, [formData, validator, validationRules]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError('');

    if (disabled || isSubmitting) return;

    const isValid = validateForm();
    if (!isValid) return;

    setIsSubmitting(true);

    try {
      await onSubmit(formData);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'An error occurred during submission';
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Clone children and inject form handling props
  const enhancedChildren = React.Children.map(children, child => {
    if (React.isValidElement(child) && (child.props as any).name) {
      const name = (child.props as any).name;
      return React.cloneElement(child as any, {
        value: formData[name] || '',
        onChange: (value: any) => {
          updateFormData(name, value);
          if ((child.props as any).onChange) {
            (child.props as any).onChange(value);
          }
        },
        onBlur: () => {
          updateTouched(name);
          if ((child.props as any).onBlur) {
            (child.props as any).onBlur();
          }
        },
        error: touched[name] ? errors[name] : '',
        touched: touched[name] || false,
      });
    }
    return child;
  });

  const hasErrors = Object.values(errors).some(error => error !== '');
  const errorList = Object.entries(errors).filter(([_, error]) => error !== '');

  return (
    <form
      onSubmit={handleSubmit}
      className={`space-y-6 ${className}`}
      noValidate
    >
      {enhancedChildren}

      <AnimatePresence>
        {showErrorSummary && hasErrors && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-red-50 border border-red-200 rounded-2xl p-4"
            role="alert"
          >
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-red-800 mb-2">
                  Please fix the following errors:
                </h3>
                <ul className="text-sm text-red-700 space-y-1">
                  {errorList.map(([field, error]) => (
                    <li key={field}>• {error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        )}

        {submitError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-red-50 border border-red-200 rounded-2xl p-4"
            role="alert"
          >
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-700">{submitError}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Button
        type="submit"
        disabled={disabled || isSubmitting}
        className={`w-full ${submitButtonClassName}`}
        variant="primary"
      >
        {isSubmitting ? (
          <div className="flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Submitting...</span>
          </div>
        ) : (
          submitButtonText
        )}
      </Button>
    </form>
  );
};

export default ValidatedForm;
